// ==================== TERMS AND CONDITIONS PAGE COMPONENT ====================

import React, { useEffect, useState } from "react";
import { getData, serverURL } from "../../../Services/Fetchnodeservices.js";
import Footer from "./Footer.js";
import Headerpage from "./Header2.js";
import {
  Shield,
  FileText,
  CheckCircle,
  Clock,
  Scale,
  Gavel,
  AlertCircle,
  BookOpen,
  Users,
  Lock,
} from "lucide-react";

const TermsAndConditions = ({ isDark }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = isDark
    ? {
        bg: "#0a0c10",
        cardBg: "#141824",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        border: "rgba(255,255,255,0.08)",
        heading: "#ffffff",
        code: "#1e293b",
        accent: "#f59e0b",
        accentLight: "#fbbf24",
      }
    : {
        bg: "#f8fafc",
        cardBg: "#ffffff",
        text: "#334155",
        textSecondary: "#64748b",
        border: "rgba(0,0,0,0.1)",
        heading: "#0f172a",
        code: "#f1f5f9",
        accent: "#f59e0b",
        accentLight: "#fcd34d",
      };

  const fetchPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("Termsconditions/Terms&conditions");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setPage(response.data);
      } else {
        setError("Terms and Conditions not found");
      }
    } catch (e) {
      console.error("Error fetching terms:", e);
      setError("Failed to load Terms and Conditions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: `3px solid ${t.border}`,
                borderTopColor: t.accent,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: t.textSecondary }}>Loading Terms and Conditions...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "20px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              backgroundColor: t.cardBg,
              padding: "40px",
              borderRadius: "20px",
              border: `1px solid ${t.border}`,
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <AlertCircle size={40} color="#ef4444" />
            </div>
            <h2 style={{ color: t.heading, marginBottom: "12px" }}>
              Terms and Conditions Not Found
            </h2>
            <p style={{ color: t.textSecondary, marginBottom: "24px" }}>
              {error}
            </p>
            <button
              onClick={fetchPage}
              style={{
                padding: "12px 24px",
                backgroundColor: t.accent,
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .terms-content {
          word-wrap: break-word;
        }
        .terms-content h1 {
          font-size: 2rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 700;
        }
        .terms-content h2 {
          font-size: 1.5rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
          border-left: 4px solid ${t.accent};
          padding-left: 1rem;
        }
        .terms-content h3 {
          font-size: 1.25rem;
          margin-top: 1.2em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
        }
        .terms-content p {
          line-height: 1.8;
          margin-bottom: 1rem;
          color: ${t.text};
        }
        .terms-content ul,
        .terms-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${t.text};
        }
        .terms-content li {
          margin: 0.5rem 0;
          line-height: 1.7;
        }
        .terms-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${t.border};
        }
        .terms-content a {
          color: ${t.accent};
          text-decoration: none;
        }
        .terms-content a:hover {
          text-decoration: underline;
        }
        .terms-content strong {
          color: ${t.heading};
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .terms-content h1 {
            font-size: 1.5rem;
          }
          .terms-content h2 {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />

        {/* Hero Section with Thumbnail Background */}
        <section
          style={{
            position: "relative",
            minHeight: "350px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#fff",
            overflow: "hidden",
          }}
        >
          {/* Background Image */}
          {page?.page_thumbnail && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${serverURL}/images/${page.page_thumbnail})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {/* Dark Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)",
                }}
              />
            </div>
          )}

          {/* Fallback Gradient if no thumbnail */}
          {!page?.page_thumbnail && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(135deg, #b45309 0%, #f59e0b 50%, #b45309 100%)",
              }}
            />
          )}

          {/* Decorative Elements */}
          <div
            style={{
              position: "absolute",
              width: "300px",
              height: "300px",
              background: "rgba(245,158,11,0.15)",
              filter: "blur(60px)",
              borderRadius: "50%",
              top: "-150px",
              left: "-100px",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "250px",
              height: "250px",
              background: "rgba(245,158,11,0.1)",
              filter: "blur(50px)",
              borderRadius: "50%",
              bottom: "-100px",
              right: "-80px",
            }}
          />

          {/* Content */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              maxWidth: "800px",
              padding: "60px 20px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                padding: "8px 20px",
                borderRadius: "40px",
                marginBottom: "24px",
              }}
            >
              <Scale size={18} />
              <span style={{ fontSize: "14px" }}>Terms & Conditions</span>
            </div>

            <h1
              style={{
                fontSize: "clamp(36px, 6vw, 56px)",
                fontWeight: "700",
                marginBottom: "20px",
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
              }}
            >
              {page?.page_title || "Terms and Conditions"}
            </h1>

            <p
              style={{
                fontSize: "16px",
                opacity: 0.9,
                marginBottom: "24px",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Please read these terms carefully before using our services
            </p>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexWrap: "wrap",
                marginTop: "24px",
              }}
            >
              {[
                { icon: Shield, text: "Secure Platform" },
                { icon: Gavel, text: "Legally Binding" },
                { icon: CheckCircle, text: "User Agreement" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                    padding: "8px 16px",
                    borderRadius: "40px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <item.icon size={16} />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            {/* Last Updated */}
            <div
              style={{
                marginTop: "32px",
                fontSize: "13px",
                opacity: 0.7,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Clock size={14} />
              <span>Last Updated: {formatDate(page?.updated_at)}</span>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section
          style={{
            padding: "60px 20px",
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: "1100px",
              margin: "0 auto",
            }}
          >
            {/* Key Highlights Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "24px",
                marginBottom: "48px",
              }}
            >
              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <BookOpen size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Read Carefully
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  These terms govern your use of Zaptodoor's services
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Users size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  User Agreement
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  By using our platform, you agree to these terms
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Lock size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Legal Compliance
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  We comply with all applicable laws and regulations
                </p>
              </div>
            </div>

            {/* Main Content Card */}
            <div
              style={{
                backgroundColor: t.cardBg,
                borderRadius: "28px",
                border: `1px solid ${t.border}`,
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ padding: "48px" }}>
                {/* Full HTML Content */}
                <div
                  className="terms-content"
                  dangerouslySetInnerHTML={{
                    __html: page?.page_description || "No content available",
                  }}
                />

                {/* Footer Information */}
                <div
                  style={{
                    marginTop: "48px",
                    paddingTop: "24px",
                    borderTop: `1px solid ${t.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px",
                    fontSize: "13px",
                    color: t.textSecondary,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FileText size={16} />
                    <span>Page ID: {page?.id}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Clock size={16} />
                    <span>Created: {formatDate(page?.created_at)}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle size={16} />
                    <span>Status: {page?.status === 1 ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Need Help Section */}
            <div
              style={{
                background: `linear-gradient(135deg, ${t.accent}15, ${t.accent}08)`,
                borderRadius: "24px",
                padding: "40px",
                marginTop: "48px",
                textAlign: "center",
                border: `1px solid ${t.border}`,
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: `${t.accent}20`,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <AlertCircle size={28} color={t.accent} />
              </div>
              <h3
                style={{
                  color: t.heading,
                  fontSize: "24px",
                  marginBottom: "12px",
                }}
              >
                Have Questions About Our Terms?
              </h3>
              <p
                style={{
                  color: t.textSecondary,
                  marginBottom: "28px",
                  fontSize: "15px",
                }}
              >
                If you have any questions about these Terms and Conditions, please contact us
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a
                  href="mailto:info@zaptodoor.com"
                  style={{
                    background: t.accent,
                    color: "white",
                    padding: "12px 28px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <FileText size={18} />
                  Email Us
                </a>
                <a
                  href="/contact"
                  style={{
                    background: "transparent",
                    color: t.text,
                    padding: "12px 28px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    border: `1px solid ${t.border}`,
                    fontWeight: "500",
                  }}
                >
                  <Users size={18} />
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TermsAndConditions;